import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';
import useAuthStore from '../stores/authStore';
import usePermissionStore from '../stores/permissionStore';
import useUIStore from '../stores/uiStore';

const SocketContext = createContext(null);

class SocketManager {
  constructor() {
    this.socket = null;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.isConnecting = false;
    this.lastEventIds = new Map(); // Track last event IDs to prevent duplicates
    this.reconnectTimer = null;
    this.manualDisconnect = false;
    this.connectionCheckInterval = null;
    this.baseDelay = 1000; // Base delay for exponential backoff (1 second)
    this.maxDelay = 30000; // Maximum delay (30 seconds)
    this.jitterFactor = 0.3; // 30% jitter
  }

  connect(token, refreshToken, onPermissionUpdate) {
    if (this.socket?.connected || this.isConnecting) return this.socket;

    this.isConnecting = true;

    const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    
    console.log('Connecting to Socket.io server at:', serverUrl);
    
    this.socket = io(serverUrl, {
      auth: { 
        token,
        refreshToken 
      },
      extraHeaders: {
        'x-refresh-token': refreshToken
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      reconnectionAttempts: Infinity,
      transports: ['websocket', 'polling'],
      upgrade: true,
      path: '/socket.io/'
    });
    
    console.log('Socket instance created');

    this.setupCoreHandlers(onPermissionUpdate);
    return this.socket;
  }

  setupCoreHandlers(onPermissionUpdate) {
    // Debug all events
    this.socket.onAny((eventName, ...args) => {
      console.log('[Socket] Event received:', eventName, args);
    });

    // Connection handlers
    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket.id);
      this.reconnectAttempts = 0;
      this.isConnecting = false;

      // Re-register any handlers that were added before connection
      this.reregisterHandlers();

      useUIStore.getState().addNotification({
        message: 'Connected to server',
        type: 'success'
      });
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      this.isConnecting = false;
      
      // Show disconnection notification
      useUIStore.getState().addNotification({
        message: 'Disconnected from server. Attempting to reconnect...',
        type: 'warning',
        duration: 7000
      });
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, attempt reconnect
        this.socket.connect();
      }
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      console.error('Error type:', error.type);
      console.error('Error details:', error);
      
      this.reconnectAttempts++;
      this.isConnecting = false;
      
      if (this.reconnectAttempts >= 10) {
        useUIStore.getState().addNotification({
          message: 'Still trying to connect to server...',
          type: 'warning',
          duration: 5000
        });
      }
    });

    // Add reconnection attempt handler
    this.socket.io.on('reconnect_attempt', (attemptNumber) => {
      console.log('Reconnection attempt:', attemptNumber);
      useUIStore.getState().addNotification({
        message: `Reconnecting... (attempt ${attemptNumber})`,
        type: 'info',
        duration: 3000
      });
    });

    // Connected confirmation
    this.socket.on('connected', (data) => {
      console.log('Socket connection confirmed:', data);
      
      // Handle token refresh if new token provided
      if (data.newToken) {
        console.log('Received refreshed token from server');
        useAuthStore.getState().setToken(data.newToken);
        useUIStore.getState().addNotification({
          message: 'Session refreshed automatically',
          type: 'success',
          duration: 3000
        });
      }
    });

    // Permission handlers
    this.socket.on('permissions:updated', (data) => {
      console.log('Permissions updated:', data);
      onPermissionUpdate(data);
      
      if (data.message) {
        useUIStore.getState().addNotification({
          message: data.message,
          type: 'info'
        });
      }
    });

    this.socket.on('auth:refresh_required', (data) => {
      console.log('Auth refresh required:', data);
      useUIStore.getState().addNotification({
        message: data.message || 'Authentication refresh required',
        type: 'warning'
      });
      
      // Wait a moment then reload
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    });

    // Scope updates
    this.socket.on('scope:item_updated', (data) => {
      console.log('Scope item updated:', data);
      if (data.message) {
        useUIStore.getState().addNotification({
          message: data.message,
          type: 'info'
        });
      }
    });

    // Error handler
    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
      useUIStore.getState().addNotification({
        message: error.message || 'Socket error occurred',
        type: 'error'
      });
    });
  }

  reregisterHandlers() {
    if (!this.socket) return;

    console.log('Re-registering event handlers after reconnection');

    // Re-register a single master listener per event type
    this.eventHandlers.forEach((handlers, event) => {
      // Remove any existing listener first
      this.socket.off(event);

      // Register a single master listener that calls all handlers
      this.socket.on(event, (data) => this.handleEvent(event, data));
      console.log(`Re-registered master listener for event: ${event}`);
    });
  }

  handleEvent(event, data) {
    // Check for duplicate events ONCE at the entry point
    if (data?.eventId) {
      const lastId = this.lastEventIds.get(event);
      if (lastId === data.eventId) {
        console.warn(`Duplicate event ${event} ignored:`, data.eventId);
        return;
      }
      this.lastEventIds.set(event, data.eventId);
    }

    // Call ALL registered handlers for this event
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in handler for ${event}:`, error);
        }
      });
    }
  }

  on(event, handler) {
    // Get or create handler set for this event
    const handlers = this.eventHandlers.get(event) || new Set();

    // Check if handler already exists (prevent duplicate registration)
    for (const h of handlers) {
      if (h === handler) {
        return; // Handler already registered
      }
    }

    // Add handler to the set
    handlers.add(handler);
    this.eventHandlers.set(event, handlers);

    // If this is the first handler for this event, register the master listener
    if (this.socket && handlers.size === 1) {
      this.socket.on(event, (data) => this.handleEvent(event, data));
    }
  }

  off(event, handler) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      // Remove the handler
      handlers.delete(handler);

      if (handlers.size === 0) {
        // No more handlers for this event, remove the master listener
        this.eventHandlers.delete(event);
        if (this.socket) {
          this.socket.off(event);
        }
      }
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Cannot emit ${event}: socket not connected`);
    }
  }

  disconnect() {
    if (this.socket) {
      // Remove all master listeners
      this.eventHandlers.forEach((handlers, event) => {
        this.socket.off(event);
      });
      
      this.eventHandlers.clear();
      this.lastEventIds.clear();
      this.socket.disconnect();
      this.socket = null;
      this.isConnecting = false;
    }
  }

  isConnected() {
    return this.socket?.connected || false;
  }
}

// Singleton instance
const socketManager = new SocketManager();

export const SocketProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const { token, refreshToken, user } = useAuthStore();
  const initAuth = useAuthStore(state => state.initAuth);

  const handlePermissionUpdate = useCallback(async (data) => {
    try {
      // Refresh user data from server (which includes permissions)
      await initAuth();
      
      // Show appropriate notification
      if (data.type === 'role_changed') {
        console.log('User role changed, refreshing permissions');
      } else if (data.type === 'scope_changed') {
        console.log('User scope changed, refreshing permissions');
      } else if (data.type === 'role_permissions_changed') {
        console.log('Role permissions changed, refreshing permissions');
      }
    } catch (error) {
      console.error('Error refreshing permissions:', error);
    }
  }, [initAuth]);

  useEffect(() => {

    if (token && refreshToken && user) {
      socketRef.current = socketManager.connect(token, refreshToken, handlePermissionUpdate);
      setConnected(socketManager.isConnected());

      // Set up connection state tracking
      const checkConnection = setInterval(() => {
        setConnected(socketManager.isConnected());
      }, 1000);

      return () => {
        clearInterval(checkConnection);
        socketManager.disconnect();
        setConnected(false);
      };
    } else {
      // No token/user, ensure disconnected
      socketManager.disconnect();
      setConnected(false);
    }
  }, [token, refreshToken, user, handlePermissionUpdate]);

  const value = {
    socket: socketRef.current,
    connected,
    on: useCallback((event, handler) => socketManager.on(event, handler), []),
    off: useCallback((event, handler) => socketManager.off(event, handler), []),
    emit: useCallback((event, data) => socketManager.emit(event, data), [])
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};

// Export for use in stores and services
export { socketManager };